import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * SaveHomeWizard
 * ──────────────
 * If the rider has finished a trip to the SAME dropoff at least 3 times
 * (within ~200m to allow for GPS noise), and doesn't yet have a Home
 * favorite there, we prompt them to save this address as "Home".
 *
 * Silent on all failure paths so it never blocks the receipt UX.
 */
export default function SaveHomeWizard({ dropoffAddress, dropoffLat, dropoffLng }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dropoffAddress || !dropoffLat || !dropoffLng) return;
    let cancelled = false;
    (async () => {
      try {
        const [ordersRes, favsRes] = await Promise.all([
          api.get("/my/orders"),
          api.get("/favorites").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const favs = favsRes.data || [];
        // Skip if a Home favorite already exists near this address
        const hasHome = favs.some((f) =>
          /^home$/i.test(f.label || "")
          || (f.lat && f.lng && Math.abs(f.lat - dropoffLat) < 0.002 && Math.abs(f.lng - dropoffLng) < 0.002)
        );
        if (hasHome) return;
        // Count how many past trips ended at this same dropoff (~200m)
        const near = (o) =>
          o.dropoff_lat && o.dropoff_lng
          && Math.abs(o.dropoff_lat - dropoffLat) < 0.002
          && Math.abs(o.dropoff_lng - dropoffLng) < 0.002;
        const times = (ordersRes.data || []).filter(near).length;
        if (times >= 3) setVisible(true);
      } catch (_) {
        // silent — feature is opt-in cue, never a blocker
      }
    })();
    return () => { cancelled = true; };
  }, [dropoffAddress, dropoffLat, dropoffLng]);

  if (!visible || dismissed) return null;

  const saveAs = async (label) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/favorites", {
        label, address: dropoffAddress, lat: dropoffLat, lng: dropoffLng,
      });
      setSaved(true);
      toast.success(t("saveHome.saved", "Saved as {{label}}", { label }));
    } catch (e) {
      toast.error(t("saveHome.failed", "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-electric-teal/30 bg-electric-teal/[0.06] p-4 relative" data-testid="save-home-wizard">
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="absolute top-2 end-2 w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5"
        aria-label={t("dismiss", "Dismiss")}
        data-testid="save-home-dismiss"
      >
        <X size={12} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-electric-teal text-obsidian flex items-center justify-center shrink-0">
          {saved ? <CheckCircle2 size={16} strokeWidth={2.5} /> : <Home size={16} strokeWidth={2.5} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">
            {saved ? t("saveHome.savedEyebrow", "Saved") : t("saveHome.eyebrow", "You visit this spot often")}
          </div>
          <div className="text-sm font-black text-white truncate">
            {saved
              ? t("saveHome.savedTitle", "One-tap booking unlocked")
              : t("saveHome.title", "Save this place for one-tap booking?")}
          </div>
          <div className="text-[11px] text-white/60 mt-0.5 truncate">{(dropoffAddress || "").split(",")[0]}</div>
        </div>
      </div>
      {!saved && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {["Home", "Work", "Other"].map((lbl) => (
            <button
              key={lbl}
              type="button"
              onClick={() => saveAs(lbl)}
              disabled={busy}
              className="rounded-full bg-electric-teal text-obsidian text-[11px] font-black px-3 py-2 hover:bg-electric-teal/90 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              data-testid={`save-home-as-${lbl.toLowerCase()}`}
            >
              {busy && <Loader2 size={11} className="animate-spin" />}
              {t(`saveHome.as${lbl}`, `Save as ${lbl}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
